import React, {useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useParams} from 'react-router-dom';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import ReplayIcon from '@mui/icons-material/Replay';

import PrimaryAppBar from './PrimaryAppBar';
import PDFViewer from './PDFViewer';
import WordWorkApp from '../word-work/components/WordWorkApp';
import WordSortApp from '../word-sort/components/WordSortApp';
import WordDivisionApp from '../word-division/components/WordDivisionApp';
import Writing from './Writing';
import {
  selectActiveExercise,
  selectSteps,
  fetchLesson,
  resetProgress,
  setActiveExercise,
  selectIsLoading,
} from '../redux/slices/lesson';
import {APP_WORD_WORK, APP_WORD_SORT, APP_WORD_DIVISION, APP_READING, APP_WRITING, APP_ASSESSMENT} from '../constants';
import {getRandomInt} from '../helpers/numberHelpers';
import {aOrAn} from '../helpers';

import ImagePenguin from '../word-work/images/clipart/animals/penguin.png';
import ImageWhale from '../word-work/images/clipart/animals/whale.png';
import ImageTiger from '../word-work/images/clipart/animals/tiger.png';
import ImageRhino from '../word-work/images/clipart/animals/rhino.png';
import ImageOctopus from '../word-work/images/clipart/animals/octopus.png';
import ImageOwl from '../word-work/images/clipart/animals/owl.png';
import ImageKoala from '../word-work/images/clipart/animals/koala.png';
import ImageMoose from '../word-work/images/clipart/animals/moose.png';
import ImageMonkey from '../word-work/images/clipart/animals/monkey.png';
import ImagePolarBear from '../word-work/images/clipart/animals/polar-bear.png';

import styles from '../css/LessonViewer.module.css';
import baseStyles from '../css/Base.module.css';

const ANIMALS_CONFIG = {
  penguin: ImagePenguin,
  whale: ImageWhale,
  tiger: ImageTiger,
  rhino: ImageRhino,
  octopus: ImageOctopus,
  owl: ImageOwl,
  koala: ImageKoala,
  moose: ImageMoose,
  monkey: ImageMonkey,
  'polar bear': ImagePolarBear,
};
const keys = Object.keys(ANIMALS_CONFIG);
const randomIndex = getRandomInt(0, keys.length - 1);
const randomAnimalName = keys[randomIndex];
const randomAnimalImage = ANIMALS_CONFIG[randomAnimalName];

export default function LessonViewer() {
  const dispatch = useDispatch();
  const steps = useSelector(selectSteps);
  const activeExercise = useSelector(selectActiveExercise);
  const isLoading = useSelector(selectIsLoading);
  const {lessonID} = useParams();

  useEffect(() => {
    dispatch(fetchLesson(lessonID));
  }, [dispatch, lessonID]);

  const isComplete = steps.every((step) => step.status === 'COMPLETE');

  function handleClickReset() {
    dispatch(resetProgress());
    dispatch(setActiveExercise());
  }

  function renderComplete() {
    return (
      <div className={[styles['center-col'], baseStyles['m-t-90']].join(' ')}>
        <img src={randomAnimalImage} alt="animal" />
        <Typography variant="h2">Great job!</Typography>
        <Typography variant="h4">{`Here! Have ${aOrAn(randomAnimalName)} ${randomAnimalName}.`}</Typography>
        <Button
          variant="outlined"
          className={[baseStyles['m-t-30'], baseStyles['btn-pill']].join(' ')}
          startIcon={<ReplayIcon />}
          onClick={handleClickReset}
        >
          START OVER
        </Button>
      </div>
    );
  }

  function renderExercise() {
    const {type} = activeExercise;
    const props = {
      mode: 'LESSON',
      exercise: activeExercise,
    };

    if ([APP_READING, APP_ASSESSMENT].includes(type)) return <PDFViewer {...props} />;
    else if (type === APP_WORD_WORK) return <WordWorkApp {...props} />;
    else if (type === APP_WORD_SORT) return <WordSortApp {...props} />;
    else if (type === APP_WORD_DIVISION) return <WordDivisionApp {...props} />;
    else if (type === APP_WRITING) return <Writing {...props} />;
    else return <div>APP TYPE {type} NOT SUPPORTED</div>;
  }

  return (
    <>
      <PrimaryAppBar showDoneButton={!isComplete} />
      {isLoading && (
        <div className={baseStyles['progress-container']}>
          <CircularProgress />
        </div>
      )}

      <div className={styles.container}>
        {!isLoading && isComplete && renderComplete()}

        {activeExercise && !isLoading && !isComplete && renderExercise()}
      </div>
    </>
  );
}
